/** Source upload endpoints (PHASE3-CONTRACT §1). The Out* data a generation run reads from. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type { SourceKind, SourceUpload } from "./types";

export function useSources() {
  return useQuery({
    queryKey: ["sources"],
    queryFn: async (): Promise<SourceUpload[]> => {
      const { data } = await api.get<SourceUpload[]>("/sources");
      return data;
    },
  });
}

export function useSource(id: string | undefined) {
  return useQuery({
    queryKey: ["source", id],
    queryFn: async (): Promise<SourceUpload> => {
      const { data } = await api.get<SourceUpload>(`/sources/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useUploadSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      file: File;
      kind: SourceKind;
    }): Promise<SourceUpload> => {
      const form = new FormData();
      form.append("file", args.file);
      form.append("kind", args.kind);
      const { data } = await api.post<SourceUpload>("/sources/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sources"] });
    },
  });
}

export function useDeleteSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.delete(`/sources/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sources"] });
    },
  });
}
